import type { BillingVariant } from '@/api/dto/activity.dto'
import type { Activity } from '@/common/activity.common'
import {
  ADVISORY_COLOR_HEX,
  PILOT_COLOR_HEX,
  TOJ_COLOR_HEX,
  TOJ_CUSTOMER_COLOR_HEX,
  WORKSHOP_COLOR_HEX,
} from '@/constants/colors.constants'
import type { EventInput } from '@fullcalendar/core/index.js'
import dayjs from 'dayjs'

function activityTypeToTitle(activityType: Activity['type']): string {
  switch (activityType) {
    case 'TRAINING':
      return 'Schulung'
    case 'CONSULTING':
      return 'Beratung'
    case 'TOJ':
      return 'TOJ'
    case 'TOJ_CUSTOMER':
      return 'TOJ mit Kunde'
    case 'PILOT':
      return 'Pilot'
    default:
      return 'Aktivität'
  }
}

function activityTypeToColor(activityType: Activity['type']): string {
  switch (activityType) {
    case 'TRAINING':
      return WORKSHOP_COLOR_HEX
    case 'CONSULTING':
      return ADVISORY_COLOR_HEX
    case 'TOJ':
      return TOJ_COLOR_HEX
    case 'TOJ_CUSTOMER':
      return TOJ_CUSTOMER_COLOR_HEX
    case 'PILOT':
      return PILOT_COLOR_HEX
    default:
      return '#666666'
  }
}

export function activityToEvent(activity: Activity): EventInput {
  return {
    id: activity.id,
    title: activityTypeToTitle(activity.type),
    // title: activity.serviceName || activity.serviceCode || 'Aktivität',
    date: activity.date,
    extendedProps: {
      color: activityTypeToColor(activity.type),
    },
    color: activityTypeToColor(activity.type),
  }
}

export function normalizeServiceCode(code?: string) {
  return String(code ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, '_')
}

export function extractOrderServiceCodes(order: any): string[] {
  const out: string[] = []

  const push = (v: any) => {
    const n = normalizeServiceCode(v)
    if (n && n !== 'UNDEFINED' && n !== 'NULL') out.push(n)
  }

  // common direct fields
  push(order?.serviceCode)
  push(order?.serviceType)
  push(order?.type)
  push(order?.activityType)

  // buckets (your structure)
  const buckets = order?.buckets
  if (Array.isArray(buckets)) {
    for (const b of buckets) {
      push(b?.serviceType)
      push(b?.serviceCode)
      push(b?.type)
      push(b?.code)
    }
  }

  // older shapes
  const lists = [
    order?.services,
    order?.items,
    order?.orderItems,
    order?.positions,
    order?.bankServices,
  ]
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const it of list) {
      push(it?.serviceCode)
      push(it?.serviceType)
      push(it?.type)
      push(it?.code)
    }
  }

  return Array.from(new Set(out))
}
export function extractOrderEntityIds(order: any): number[] {
  const out = new Set<number>()

  const push = (v: any) => {
    const n = Number(v)
    if (Number.isFinite(n) && n > 0) out.add(n)
  }

  // direct fields
  push(order?.customerId)
  push(order?.recipientId)
  push(order?.serviceRecipientId)
  push(order?.associatedEntityId)
  push(order?.entityId)
  push(order?.bankId)

  // common array shapes
  const arrays = [
    order?.associatedEntities,
    order?.entities,
    order?.recipients,
    order?.serviceRecipients,
    order?.participants,
  ]

  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue
    for (const it of arr) {
      push(it?.id)
      push(it?.entityId)
      push(it?.bankId)
      push(it?.customerId)
    }
  }

  // buckets / items / services
  const lists = [
    order?.buckets,
    order?.services,
    order?.items,
    order?.orderItems,
    order?.positions,
    order?.bankServices,
  ]

  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const it of list) {
      push(it?.entityId)
      push(it?.recipientId)
      push(it?.serviceRecipientId)
      push(it?.bankId)
      push(it?.customerId)
      // sometimes nested:
      push(it?.entity?.id)
      push(it?.recipient?.id)
    }
  }

  return Array.from(out)
}

export function resolveApprovedOrderId(args: {
  acceptedOrders: any[]
  unionId: number
  selectedEntityId: number
  selectedServiceCode: string
}) {
  const { acceptedOrders, unionId, selectedEntityId, selectedServiceCode } = args

  const svcRaw = normalizeServiceCode(selectedServiceCode)
  const svc = svcRaw === 'TOJ_CUSTOMER' ? 'TOJ' : svcRaw

  const approved = (acceptedOrders ?? []).filter(
    o => String(o?.status).toUpperCase() === 'APPROVED'
  )

  const unionCandidates = approved.filter(o => {
    const b = Number(o?.bankId)
    const c = Number(o?.customerId)
    return b === unionId || c === unionId
  })

  const candidates = unionCandidates.filter(o => {
    const entityIds = extractOrderEntityIds(o)
    if (!entityIds.includes(Number(selectedEntityId))) return false

    const svcCodes = extractOrderServiceCodes(o)
    return svcCodes.includes(svc)
  })

  if (candidates.length === 0) return undefined

  const sorted = [...candidates].sort((a, b) => {
    const da = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
    const db = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime()
    return db - da
  })

  return Number(sorted[0].id)
}

/** ---------- service normalization ---------- */

export function normalizeSvc(code?: string) {
  return String(code ?? '')
    .trim()
    .toUpperCase()
}

export function normalizeServiceForBilling(code?: string) {
  const c = normalizeSvc(code)
  return c === 'TOJ_CUSTOMER' ? 'TOJ' : c
}

export function isTojService(code?: string) {
  const c = normalizeSvc(code)
  return c === 'TOJ' || c === 'TOJ_CUSTOMER'
}

export function isConsultingService(code?: string) {
  return normalizeSvc(code) === 'CONSULTING'
}

/**
 * Advisor number (Beraternummer) availability for Union activities.
 * Editable for TOJ and consulting hours; mandatory only for TOJ (SIB-926).
 */
export function getAdvisorNumberState(isUnionSelected: boolean, code?: string) {
  const toj = isTojService(code)
  const editable = isUnionSelected && (toj || isConsultingService(code))
  return { editable, required: isUnionSelected && toj }
}

/** ---------- qty constraints + snapping ---------- */

export function getQtyConstraints(code?: string): { min: number; step: number; max?: number } {
  const svc = normalizeServiceForBilling(code)
  if (svc === 'TOJ') return { min: 1, step: 1 }
  if (svc === 'CONSULTING') return { min: 0.5, step: 0.5, max: 10 }
  if (svc === 'TRAINING') return { min: 0.5, step: 0.5, max: 1 }
  if (svc === 'PILOT') return { min: 1, step: 1 }
  return { min: 1, step: 1 }
}

export function clampAndSnapQty(raw: string, code?: string): number | '' {
  const { min, step, max } = getQtyConstraints(code)
  if (raw === '') return ''
  let v = Number(raw)
  if (!Number.isFinite(v)) return ''

  if (v < min) v = min
  if (max != null && v > max) v = max

  const svc = normalizeServiceForBilling(code)

  if (svc === 'TOJ' || svc === 'PILOT') {
    v = Math.round(v)
    if (v < 1) v = 1
    return v
  }

  if (svc === 'CONSULTING') {
    v = Math.round(v * 2) / 2
    return v
  }

  if (svc === 'TRAINING') {
    return v <= 0.5 ? 0.5 : 1
  }

  // fallback: snap to step
  v = Math.round(v / step) * step
  return v
}

/** ---------- billing variant logic ---------- */

export function eightyPercentVariantForService(code?: string): BillingVariant {
  switch (normalizeServiceForBilling(code)) {
    case 'TOJ':
      return 'EIGHTY_PERCENT_TOJ'
    case 'CONSULTING':
      return 'EIGHTY_PERCENT_CONSULTING'
    case 'TRAINING':
      return 'EIGHTY_PERCENT_TRAINING'
    case 'PILOT':
      return 'EIGHTY_PERCENT_PILOT'
    default:
      return 'STANDARD'
  }
}

export function computeBillingVariant(params: {
  serviceCode?: string
  qty?: number
  shortNoticeCancelled?: boolean
}): BillingVariant {
  const { serviceCode, qty, shortNoticeCancelled } = params
  const svc = normalizeServiceForBilling(serviceCode)

  if (shortNoticeCancelled) return eightyPercentVariantForService(svc)
  if (svc === 'TRAINING' && qty === 0.5) return 'HALF_TRAINING_DAY'

  return 'STANDARD'
}

/** ---------- advisor number ---------- */

export function digitsOnly(s: string) {
  return (s ?? '').replace(/\D/g, '')
}

export function padAdvisorNumber(s: string) {
  const d = digitsOnly(s).slice(0, 8)
  return d.padStart(8, '0')
}

/** ---------- time helpers ---------- */

export function timeToMinutes(t?: string) {
  if (!t) return null
  const s = t.trim()
  const [hh, mm] = s.split(':')
  const h = Number(hh)
  const m = Number(mm)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

export function toHHMMSS(v: string) {
  if (!v) return ''
  return v.length === 5 ? `${v}:00` : v
}

export function toHHMM(v: string) {
  if (!v) return ''
  return v.slice(0, 5)
}

/** ---------- DTO conversion helpers ---------- */

export function uiQtyFromForm(values: any): number {
  const q =
    values?.services?.[0]?.quantity !== ''
      ? Number(values?.services?.[0]?.quantity)
      : Number(values?.timeQuantity ?? 0)
  return Number.isFinite(q) ? q : 0
}

export function formatActivityDate(d: Date) {
  return dayjs(d).format('YYYY-MM-DD[T]HH:mm:ss')
}

export function getQtyHint(code?: string) {
  const svc = normalizeServiceForBilling(code)

  if (svc === 'TOJ') return 'Nur ganze Zahlen erlaubt: 1, 2, 3, …'
  if (svc === 'CONSULTING') return 'Erlaubt: 0,5 bis 10 in 0,5er Schritten.'
  if (svc === 'TRAINING') return 'Erlaubt: nur 0,5 oder 1.'
  if (svc === 'PILOT') return 'Nur ganze Zahlen erlaubt: 1, 2, 3, …'
  return ''
}
export function roundUpToNextQuarterHour(date = new Date()) {
  const d = new Date(date)
  d.setSeconds(0, 0)

  const minutes = d.getMinutes()
  const rounded = Math.ceil(minutes / 15) * 15

  if (rounded === 60) {
    d.setHours(d.getHours() + 1, 0, 0, 0)
  } else {
    d.setMinutes(rounded, 0, 0)
  }

  return d
}

export function formatTimeHHmm(date: Date) {
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

export function addMinutesToHHmm(time: string, minutesToAdd: number) {
  const [hh, mm] = time.split(':').map(Number)
  const total = hh * 60 + mm + minutesToAdd
  const normalized = ((total % (24 * 60)) + 24 * 60) % (24 * 60)
  const h = Math.floor(normalized / 60)
  const m = normalized % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function getAutoDurationMinutes(
  serviceCode?: string,
  qty?: number,
  isUnionSelected?: boolean
) {
  const code = String(serviceCode ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, '_')

  const quantity = Number(qty ?? 0)
  if (!Number.isFinite(quantity) || quantity <= 0) return null

  // non-Union: always 1 unit = 1 hour
  if (!isUnionSelected) {
    return quantity * 60
  }

  // Union-specific rules
  switch (code) {
    case 'TOJ':
    case 'TOJ_CUSTOMER':
      return quantity * 150
    case 'CONSULTING':
      return quantity * 60
    case 'TRAINING':
      return quantity * 480
    case 'PILOT':
      return quantity * 60
    default:
      return quantity * 60
  }
}
export function isInvoicedActivityConflict(error: unknown) {
  if (!(error instanceof Error)) return false

  const msg = error.message.toLowerCase()

  return (
    msg.includes('already been invoiced') ||
    msg.includes('cannot be modified') ||
    msg.includes('cannot be deleted') ||
    msg.includes('bereits abgerechnet')
  )
}

export function getInvoicedActivityConflictMessage(error: unknown) {
  if (!(error instanceof Error)) {
    return 'Diese Aktivität wurde bereits abgerechnet und kann nicht mehr geändert oder gelöscht werden.'
  }

  const msg = error.message.toLowerCase()

  if (msg.includes('cannot be deleted')) {
    return 'Diese Aktivität wurde bereits abgerechnet und kann nicht gelöscht werden.'
  }

  if (msg.includes('cannot be modified')) {
    return 'Diese Aktivität wurde bereits abgerechnet und kann nicht bearbeitet werden.'
  }

  return 'Diese Aktivität wurde bereits abgerechnet und kann nicht mehr geändert oder gelöscht werden.'
}

export function toVisibleAdvisorNumber(value?: string | null) {
  const digits = digitsOnly(String(value ?? ''))
  return digits.replace(/^0+/, '')
}
